import { useState } from 'react';
import { Download, X, Smartphone, Share } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/auth';
import { useTutorialState } from '@/hooks/useTutorialState';
import { usePwaPromptPreference } from '@/hooks/usePwaPromptPreference';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export const InstallPWAPrompt = () => {
  const { isInstallable, isIOS, isInstalled, promptInstall, dismissInstall } = usePWAInstall();
  const [isVisible, setIsVisible] = useState(true);
  const [showDismissDialog, setShowDismissDialog] = useState(false);
  const { user } = useAuth();
  const { shouldShowTutorial, isLoading: tutorialLoading } = useTutorialState(user?.id);
  const { preference, isLoading: prefLoading, updatePreference } = usePwaPromptPreference();

  // Don't show if:
  // - Already installed or not installable
  // - User not logged in
  // - Tutorial not completed yet (shouldShowTutorial = true means tutorial NOT completed)
  // - Still loading tutorial state or preference
  // - User chose "never" show again
  const shouldHide = 
    isInstalled || 
    (!isInstallable && !isIOS) || 
    !isVisible || 
    !user || 
    tutorialLoading || 
    prefLoading ||
    shouldShowTutorial ||
    preference === 'never';

  if (shouldHide) {
    return null;
  }

  const handleInstall = async () => {
    const success = await promptInstall();
    if (success) {
      setIsVisible(false);
    }
  };

  const handleDismissClick = () => {
    setShowDismissDialog(true);
  };

  const handleRemindLater = async () => {
    // Just close the popup for this session, but it will appear again next time
    await updatePreference('remind_later');
    setIsVisible(false);
    setShowDismissDialog(false);
  };

  const handleNeverRemind = async () => {
    // Save preference and call dismissInstall for localStorage compatibility
    await updatePreference('never');
    dismissInstall();
    setIsVisible(false);
    setShowDismissDialog(false);
  };

  // iOS-specific instructions
  if (isIOS) {
    return (
      <>
        <AnimatePresence>
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-4 left-4 right-4 z-50 bg-card border shadow-lg rounded-lg p-4 max-w-md mx-auto"
          >
            <button
              onClick={handleDismissClick}
              className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-foreground"
              aria-label="Fechar"
            >
              <X className="w-4 h-4" />
            </button>
            
            <div className="flex items-start gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Smartphone className="w-6 h-6 text-primary" />
              </div>
              
              <div className="flex-1">
                <h3 className="font-semibold text-sm">Instalar Planific</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Toque em <Share className="w-3 h-3 inline mx-1" /> e depois em "Adicionar à Tela de Início"
                </p>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        <AlertDialog open={showDismissDialog} onOpenChange={setShowDismissDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Lembrete de instalação</AlertDialogTitle>
              <AlertDialogDescription>
                Deseja que lembremos novamente sobre a instalação do app?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col sm:flex-row gap-2">
              <AlertDialogAction onClick={handleRemindLater} className="w-full sm:w-auto">
                Ok, lembre-me mais tarde
              </AlertDialogAction>
              <AlertDialogCancel onClick={handleNeverRemind} className="w-full sm:w-auto">
                Não me lembrar mais
              </AlertDialogCancel>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  // Standard install prompt for other browsers
  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-4 left-4 right-4 z-50 bg-card border shadow-lg rounded-lg p-4 max-w-md mx-auto"
        >
          <button
            onClick={handleDismissClick}
            className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Fechar prompt de instalação"
          >
            <X className="w-4 h-4" />
          </button>
          
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Download className="w-6 h-6 text-primary" />
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm truncate">Instalar Planific</h3>
              <p className="text-xs text-muted-foreground">
                Acesse mais rápido na sua tela inicial
              </p>
            </div>
            
            <Button 
              size="sm" 
              onClick={handleInstall}
              className="shrink-0"
              aria-label="Instalar aplicativo"
            >
              Instalar
            </Button>
          </div>
        </motion.div>
      </AnimatePresence>

      <AlertDialog open={showDismissDialog} onOpenChange={setShowDismissDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Lembrete de instalação</AlertDialogTitle>
            <AlertDialogDescription>
              Deseja que lembremos novamente sobre a instalação do app?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogAction onClick={handleRemindLater} className="w-full sm:w-auto">
              Ok, lembre-me mais tarde
            </AlertDialogAction>
            <AlertDialogCancel onClick={handleNeverRemind} className="w-full sm:w-auto">
              Não me lembrar mais
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
